import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { envStrToBoolean, getSkinId, setParams } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { gameUrlRequest } from 'src/services/gameApi';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { RootState } from 'src/types/store-types';
import { setSelectedCategory } from '../../../store/app/actions';
import './styles.scss';

type Props = {
  sportType: 'live' | 'sport';
};

type FrameMessageType = 'login' | 'register' | 'deposit';

const getOddFormat = (): string | undefined => process.env.REACT_APP_BC_ODDS_TYPE?.trim() || undefined;

const getTimeFormat = (): '12h' | '24h' | undefined => {
  const timeFormat = process.env.REACT_APP_BC_TIME_FORMAT?.trim();
  return timeFormat === '12h' || timeFormat === '24h' ? timeFormat : undefined;
};
const isDepositEventEnabled = (): boolean => {
  return !envStrToBoolean(process.env.REACT_APP_BC_DEPOSIT_EVENT_DISABLED);
};

const langCodes = {
  en: 'en',
  'ar-lb': 'ar',
};

const BetConstructSportsbook: FC<Props> = ({ sportType }) => {
  const { locale } = useI18n();
  const { user } = useSelector((state: RootState) => state.user);
  const skinId = getSkinId();
  const navigate = useNavigate();

  const _openAuthModal = useConnectedAction(openAuthModal);
  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const userRef = useRef(user);
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [isIframeReady, setIsIframeReady] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const isLoginRequestedFromSportsbook = useRef<boolean>(false);
  const previousAuthToken = useRef<string | undefined>(user?.token);
  const wasLoggedIn = useRef<boolean>(Boolean(user?.token));
  const iframeRequestSeq = useRef<number>(0);
  const iframeOrigin = (() => {
    try {
      return iframeSrc ? new URL(iframeSrc).origin : undefined;
    } catch {
      return undefined;
    }
  })();

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const prevToken = previousAuthToken.current;
    const nextToken = user?.token;

    if (!prevToken && !!nextToken) {
      isLoginRequestedFromSportsbook.current = true;
    }

    previousAuthToken.current = nextToken;
  }, [user?.token]);

  const postToIframe = (payload: Record<string, unknown>): void => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow || !iframeOrigin) return;
    console.log('iframeWindow', iframeWindow);
    console.log('payload', payload);
    iframeWindow.postMessage(payload, iframeOrigin);
  };

  const postUserSession = (): void => {
    const userId = userRef.current?.id;
    const token = userRef.current?.token;

    if (!userId || !token) {
      isLoginRequestedFromSportsbook.current = false;

      postToIframe({ type: 'logout' });
      return;
    }

    const userIdStr = String(userId);

    const shouldAttachPlayer = isLoginRequestedFromSportsbook.current;
    if (!shouldAttachPlayer) return;

    postToIframe({
      type: 'playerAttach',
      externalPlayerId: userIdStr,
    });

    isLoginRequestedFromSportsbook.current = false;
  };

  const postPreferences = (): void => {
    const oddFormat = getOddFormat();
    const timeFormat = getTimeFormat();
    const data: Record<string, string> = {
      language: langCodes[locale] ?? 'en',
    };
    if (oddFormat) data.oddFormat = oddFormat;
    if (timeFormat) data.timeFormat = timeFormat;

    postToIframe({
      type: 'setPreferences',
      data,
    });
  };

  const openDeposit = useCallback((): void => {
    if (userRef.current) {
      _openAccountModal({ section: 'deposit', subSection: 'deposit' });
      return;
    }
    _openAuthModal('login');
  }, [_openAccountModal, _openAuthModal]);

  const requestSportsbookUrl = useCallback(async () => {
    const basePayload = setParams(userRef.current, { id: 100 }, skinId, langCodes[locale] ?? 'en', isMobile);
    const payload = {
      ...basePayload,
      ...(sportType === 'sport' && { launchType: 'pre-match' }),
      ...(sportType === 'live' && { launchType: 'live' }),
    };
    return gameUrlRequest(payload);
  }, [locale, skinId, sportType]);

  const refreshIframeSrc = useCallback(async (): Promise<void> => {
    const currentRequestSeq = ++iframeRequestSeq.current;
    const response = await requestSportsbookUrl();
    if (currentRequestSeq !== iframeRequestSeq.current) return;
    if (response?.success === false) {
      const key = response?.message?.key ?? 'unknown_error';
      setErrorMessage(key);
      setIsIframeReady(false);
      setIframeSrc('');
      return;
    }
    const url = response?.result?.data?.url || '';
    setErrorMessage('');
    setIsIframeReady(false);
    setIframeSrc(url);
  }, [requestSportsbookUrl]);

  useEffect(() => {
    const isLoggedInNow = Boolean(user?.token);
    if (wasLoggedIn.current !== isLoggedInNow) {
      refreshIframeSrc().catch((e) => console.error(e));
    }
    wasLoggedIn.current = isLoggedInNow;
  }, [user?.token, refreshIframeSrc]);

  useEffect(() => {
    const resolveIframeSrc = async (): Promise<void> => {
      try {
        await refreshIframeSrc();
      } catch {
        setIsIframeReady(false);
        setIframeSrc('');
      }
    };

    resolveIframeSrc().catch((e) => console.error(e));
  }, [refreshIframeSrc]);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const { data } = event;
      if (!data) return;
      if (!iframeOrigin || event.origin !== iframeOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const type = data.event as FrameMessageType | undefined;
      if (type === 'login') {
        isLoginRequestedFromSportsbook.current = true;
        _openAuthModal('login');
        return;
      }
      if (type === 'register') {
        _setSelectedCategory('');
        navigate('/signup');
        return;
      }
      if (type === 'deposit' && isDepositEventEnabled()) {
        openDeposit();
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [navigate, _openAccountModal, _openAuthModal, openDeposit, iframeOrigin]);

  useEffect(() => {
    if (!isIframeReady) return;
    postUserSession();
  }, [isIframeReady, user?.token, user?.id]);

  useEffect(() => {
    if (!isIframeReady) return;
    postPreferences();
  }, [isIframeReady, locale]);

  if (!iframeSrc) {
    return (
      <div className="sport-book-page">
        <p>Sportsbook config is missing.</p>

        {errorMessage && <p>{errorMessage}</p>}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      id="betConstract"
      width={'100%'}
      src={iframeSrc}
      onLoad={() => {
        setIsIframeReady(true);
      }}
    />
  );
};

export default BetConstructSportsbook;
